import { BarcodeFormat, BrowserMultiFormatReader, DecodeHintType } from '@zxing/library';

const formats = ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39', 'itf', 'qr_code'];
const cameraOptions = [
  { audio: false, video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } } },
  { audio: false, video: { facingMode: { ideal: 'environment' } } },
  { audio: false, video: true },
];

export const cameraErrorMessage = (error) => {
  if (['NotAllowedError', 'SecurityError'].includes(error?.name)) return '相機權限未開啟，請在瀏覽器設定允許相機，再按重新掃描。';
  if (error?.name === 'NotFoundError') return '找不到可用相機，請確認裝置有相機，或直接輸入條碼。';
  return '無法啟動相機，請使用 HTTPS 網址，關閉其他使用相機的 App，再按重新掃描。';
};

// Each session owns its stream. Late getUserMedia results cannot escape cleanup.
export function createBarcodeCamera({ video, onResult, detect = true, onStatus = () => {}, onTrack = () => {}, onBarcode = () => {},
  onError = () => {}, mediaDevices = navigator.mediaDevices, Detector = window.BarcodeDetector,
  requestFrame = window.requestAnimationFrame.bind(window), cancelFrame = window.cancelAnimationFrame.bind(window),
  readerFactory = () => {
    const hints = new Map([[DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.EAN_13, BarcodeFormat.EAN_8,
      BarcodeFormat.UPC_A, BarcodeFormat.UPC_E, BarcodeFormat.CODE_128, BarcodeFormat.CODE_39, BarcodeFormat.ITF, BarcodeFormat.QR_CODE]],
    [DecodeHintType.TRY_HARDER, true]]);
    return new BrowserMultiFormatReader(hints, 100);
  } }) {
  let closed = false;
  let accepted = false;
  let stream;
  let reader;
  let frame;
  let cancelReady;
  const stopTracks = (value) => value?.getTracks?.().forEach((track) => track.stop());
  const stop = () => {
    closed = true;
    if (frame != null) cancelFrame(frame);
    cancelReady?.();
    reader?.reset();
    stopTracks(stream);
    if (video.srcObject === stream) video.srcObject = null;
  };
  const accept = (text, barcode) => {
    const value = String(text || '').trim();
    if (closed || accepted || !value) return;
    accepted = true;
    onBarcode(barcode);
    stop();
    onResult(value);
  };
  const ready = () => new Promise((resolve, reject) => {
    if (closed || video.readyState >= 2) return resolve();
    let timeout;
    const cleanup = () => { clearTimeout(timeout); video.removeEventListener('canplay', done); cancelReady = null; };
    const done = () => { cleanup(); resolve(); };
    cancelReady = done;
    video.addEventListener('canplay', done);
    timeout = setTimeout(() => { cleanup(); reject(new Error('Camera preview timed out')); }, 5000);
  });
  const startFallback = async () => {
    if (closed) return;
    onStatus('相容掃描中：請把條碼放進框內');
    reader = readerFactory();
    await reader.decodeFromStream(stream, video, (result) => {
      if (result) accept(result.getText());
    });
    if (closed) reader.reset();
  };
  const start = async () => {
    try {
      if (closed) return;
      if (!mediaDevices?.getUserMedia) throw new Error('Camera requires a secure context');
      onStatus('正在開啟後鏡頭…');
      for (let index = 0; index < cameraOptions.length; index += 1) {
        try { stream = await mediaDevices.getUserMedia(cameraOptions[index]); break; }
        catch (error) {
          if (closed) return;
          if (!['OverconstrainedError', 'ConstraintNotSatisfiedError'].includes(error.name) || index === cameraOptions.length - 1) throw error;
        }
      }
      if (closed) { stopTracks(stream); return; }
      video.srcObject = stream;
      video.setAttribute('playsinline', 'true');
      video.muted = true;
      await video.play();
      await ready();
      if (closed) return;
      const track = stream.getVideoTracks?.()[0];
      onTrack(track);
      if (!detect) { onStatus('相機已開啟，請將商品放入畫面'); return; }
      let supported = [];
      try { supported = Detector ? (Detector.getSupportedFormats ? await Detector.getSupportedFormats() : formats) : []; }
      catch (_) { /* Native detection is optional; ZXing uses the same stream. */ }
      if (closed) return;
      const nativeFormats = formats.filter((format) => supported.includes(format));
      let detector;
      try { if (nativeFormats.length) detector = new Detector({ formats: nativeFormats }); }
      catch (_) { /* Use the supported fallback. */ }
      if (!detector) { await startFallback(); return; }
      onStatus('快速掃描中：請把條碼放進框內');
      const scan = async () => {
        if (closed) return;
        try {
          const results = await detector.detect(video);
          if (closed) return;
          const found = results.find((item) => item.rawValue && item.format !== 'qr_code') || results.find((item) => item.rawValue);
          if (found) { accept(found.rawValue, found); return; }
          frame = requestFrame(scan);
        } catch (error) {
          if (closed) return;
          try { await startFallback(); } catch (fallbackError) { stop(); onError(fallbackError); }
        }
      };
      frame = requestFrame(scan);
    } catch (error) {
      if (!closed) { stop(); onError(error); }
    }
  };
  return { start, stop };
}
